/**
 * FeatureList Component
 *
 * Displays a list of features with filtering by category and sorting by confidence
 */

import * as React from 'react';
import { FeatureCard } from './FeatureCard';
import { FeatureDetailModal } from './FeatureDetailModal';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Filter, SortDesc, SortAsc } from 'lucide-react';
import type { Feature, FeatureCategory } from '@forgenetwork/types';

interface FeatureListProps {
  features: Feature[];
  isLoading?: boolean;
  onFeatureClick?: (feature: Feature) => void;
}

type SortOrder = 'asc' | 'desc';
type FilterCategory = FeatureCategory | 'all';

/**
 * All available feature categories plus 'all'
 */
const FILTER_OPTIONS: { value: FilterCategory; label: string }[] = [
  { value: 'all', label: 'All Categories' },
  { value: 'authentication', label: 'Authentication' },
  { value: 'authorization', label: 'Authorization' },
  { value: 'data-storage', label: 'Data Storage' },
  { value: 'api', label: 'API' },
  { value: 'ui', label: 'UI' },
  { value: 'business-logic', label: 'Business Logic' },
  { value: 'integration', label: 'Integration' },
  { value: 'infrastructure', label: 'Infrastructure' },
  { value: 'testing', label: 'Testing' },
  { value: 'other', label: 'Other' },
];

/**
 * FeatureList - Main component for displaying and managing features
 */
export function FeatureList({
  features,
  isLoading = false,
  onFeatureClick,
}: FeatureListProps): React.JSX.Element {
  const [selectedFeature, setSelectedFeature] = React.useState<Feature | null>(null);
  const [filterCategory, setFilterCategory] = React.useState<FilterCategory>('all');
  const [sortOrder, setSortOrder] = React.useState<SortOrder>('desc');

  // Filter features by category
  const filteredFeatures = React.useMemo(() => {
    if (filterCategory === 'all') {
      return features;
    }
    return features.filter((feature) => feature.category === filterCategory);
  }, [features, filterCategory]);

  // Sort features by confidence score
  const sortedFeatures = React.useMemo(() => {
    const sorted = [...filteredFeatures].sort((a, b) => {
      if (sortOrder === 'desc') {
        return b.confidence - a.confidence;
      }
      return a.confidence - b.confidence;
    });
    return sorted;
  }, [filteredFeatures, sortOrder]);

  const handleFeatureClick = (feature: Feature): void => {
    setSelectedFeature(feature);
    onFeatureClick?.(feature);
  };

  const toggleSortOrder = (): void => {
    setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="h-10 w-48 bg-muted animate-pulse rounded-md" />
          <div className="h-10 w-32 bg-muted animate-pulse rounded-md" />
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-64 bg-muted animate-pulse rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (features.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <div className="p-8 rounded-lg bg-muted/50 border-2 border-dashed">
          <Filter className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
          <h3 className="text-lg font-semibold mb-2">No Features Found</h3>
          <p className="text-sm text-muted-foreground max-w-md">
            No features have been extracted for this repository yet. Run feature extraction
            to analyze the codebase and detect features.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters and Sorting */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Filter className="h-5 w-5 text-muted-foreground" />
          <Select
            value={filterCategory}
            onValueChange={(value) => setFilterCategory(value as FilterCategory)}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Filter by category" />
            </SelectTrigger>
            <SelectContent>
              {FILTER_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            {sortedFeatures.length} {sortedFeatures.length === 1 ? 'feature' : 'features'}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={toggleSortOrder}
            className="gap-2"
          >
            {sortOrder === 'desc' ? (
              <>
                <SortDesc className="h-4 w-4" />
                High to Low
              </>
            ) : (
              <>
                <SortAsc className="h-4 w-4" />
                Low to High
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Feature Cards Grid */}
      {sortedFeatures.length === 0 ? (
        <div className="flex flex-col items-center justify-center min-h-[300px] text-center">
          <div className="p-8 rounded-lg bg-muted/50 border-2 border-dashed">
            <Filter className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-semibold mb-2">No Matching Features</h3>
            <p className="text-sm text-muted-foreground max-w-md">
              No features match the selected category. Try changing the filter.
            </p>
          </div>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {sortedFeatures.map((feature) => (
            <FeatureCard
              key={feature.id}
              feature={feature}
              onClick={handleFeatureClick}
            />
          ))}
        </div>
      )}

      {/* Detail Modal */}
      <FeatureDetailModal
        feature={selectedFeature}
        open={!!selectedFeature}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedFeature(null);
          }
        }}
      />
    </div>
  );
}
